import { apiClient } from './client';
import type { DailyProgress, MetricAggregation, StreakResult } from '@/lib/scorecards/daily';
import type { MetricStatus, MetricDirection } from '@/lib/scorecards/status';

/**
 * Client for `/api/scorecards/*`. Stays in lockstep with
 * `server/src/routes/scorecards.routes.ts`.
 */

export type MetricUnit = 'count' | 'dollars' | 'percent' | 'ratio' | 'custom';
export type MetricFrequency = 'weekly' | 'monthly';
export type MetricEntrySource = 'manual' | 'integration' | 'computed';

export type DerivedMetricKey = 'orders_shipped' | 'units_shipped' | 'lines_picked' | 'internal_ops_pct';

export type MetricDataSourceConfig =
  | { kind: 'manual' }
  | { kind: 'integration'; providerId: string; metricKey: string }
  /** ADR-048: computed per day from the ShipEdge reports cache (allowlisted org). */
  | { kind: 'derived'; source: 'shipedge_reports'; metricKey: DerivedMetricKey };

export interface Metric {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  unit: MetricUnit;
  unitLabel: string | null;
  goal: number;
  direction: MetricDirection;
  frequency: MetricFrequency;
  ownerEmployeeId: string | null;
  ownerEmployee: { id: string; firstName: string; lastName: string } | null;
  departmentId: string | null;
  /** Seat accountable for this measurable (ADR-028), with its current holder. */
  seatId: string | null;
  seat: {
    id: string;
    name: string;
    holderEmployee: { id: string; firstName: string; lastName: string } | null;
  } | null;
  dataSourceConfig: MetricDataSourceConfig;
  isActive: boolean;
  order: number;
  // Daily grain (ADR-048). `none` = no daily concept.
  dailyAggregation: MetricAggregation;
  dailyGoal: number | null;
  /** ISO weekdays (1 = Mon .. 7 = Sun) the daily goal applies on. */
  goalWeekdays: number[];
  reportingTimeZone: string;
  weeklyFromDaily: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MetricEntry {
  id: string;
  metricId: string;
  weekOf: string;
  value: number;
  notes: string | null;
  enteredViaSource: MetricEntrySource;
  enteredByEmployeeId: string | null;
  status: MetricStatus;
  /** ADR-048: a human hand-entered this week on a rolled-up metric. */
  weeklyOverrideAt: string | null;
  daysCounted: number | null;
  isOverridden: boolean;
  /** What the dailies say the week is worth; null when not rolled up. */
  rollupValue: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListMetricsQuery {
  includeInactive?: boolean;
  /** Filter to a single seat's measurables (ADR-028). */
  seatId?: string;
  /** Filter to a single department's measurables. */
  departmentId?: string;
}

export async function listMetrics(q: ListMetricsQuery = {}): Promise<Metric[]> {
  const params = new URLSearchParams();
  if (q.includeInactive) params.set('includeInactive', 'true');
  if (q.seatId) params.set('seatId', q.seatId);
  if (q.departmentId) params.set('departmentId', q.departmentId);
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: Metric[] }>(`/api/scorecards/metrics${qs}`);
  return res.data.data;
}

export interface CreateMetricRequest {
  title: string;
  description?: string;
  unit?: MetricUnit;
  unitLabel?: string;
  goal: number;
  direction?: MetricDirection;
  frequency?: MetricFrequency;
  ownerEmployeeId?: string;
  departmentId?: string;
  seatId?: string;
  order?: number;
  dataSourceConfig?: MetricDataSourceConfig;
  dailyAggregation?: MetricAggregation;
  dailyGoal?: number | null;
  goalWeekdays?: number[];
  weeklyFromDaily?: boolean;
}

export async function createMetric(body: CreateMetricRequest): Promise<Metric> {
  const res = await apiClient.post<{ data: Metric }>('/api/scorecards/metrics', body);
  return res.data.data;
}

export interface UpdateMetricRequest {
  title?: string;
  description?: string | null;
  unit?: MetricUnit;
  unitLabel?: string | null;
  goal?: number;
  direction?: MetricDirection;
  frequency?: MetricFrequency;
  ownerEmployeeId?: string | null;
  departmentId?: string | null;
  seatId?: string | null;
  order?: number;
  isActive?: boolean;
  dataSourceConfig?: MetricDataSourceConfig;
  dailyAggregation?: MetricAggregation;
  dailyGoal?: number | null;
  goalWeekdays?: number[];
  weeklyFromDaily?: boolean;
}

export async function updateMetric(id: string, body: UpdateMetricRequest): Promise<Metric> {
  const res = await apiClient.put<{ data: Metric }>(
    `/api/scorecards/metrics/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

export async function deleteMetric(id: string): Promise<void> {
  await apiClient.delete(`/api/scorecards/metrics/${encodeURIComponent(id)}`);
}

export async function listMetricEntries(metricId: string): Promise<MetricEntry[]> {
  const res = await apiClient.get<{ data: MetricEntry[] }>(
    `/api/scorecards/metrics/${encodeURIComponent(metricId)}/entries`,
  );
  return res.data.data;
}

export interface UpsertEntryRequest {
  weekOf: string; // YYYY-MM-DD, must be a Monday (UTC)
  value: number;
  notes?: string | null;
}

export async function upsertMetricEntry(metricId: string, body: UpsertEntryRequest): Promise<MetricEntry> {
  const res = await apiClient.put<{ data: MetricEntry }>(
    `/api/scorecards/metrics/${encodeURIComponent(metricId)}/entries`,
    body,
  );
  return res.data.data;
}

export async function deleteMetricEntry(metricId: string, weekOf: string): Promise<void> {
  await apiClient.delete(
    `/api/scorecards/metrics/${encodeURIComponent(metricId)}/entries/${encodeURIComponent(weekOf)}`,
  );
}

// ── Daily grain (ADR-048) — lockstep with server/src/routes/scorecards-daily.routes.ts ──

export interface ScorecardSettings {
  defaultGoalWeekdays: number[];
}

export async function getScorecardSettings(): Promise<ScorecardSettings> {
  const res = await apiClient.get<{ data: ScorecardSettings }>('/api/scorecards/settings');
  return res.data.data;
}

/** Admin-only. */
export async function updateScorecardSettings(body: Partial<ScorecardSettings>): Promise<ScorecardSettings> {
  const res = await apiClient.put<{ data: ScorecardSettings }>('/api/scorecards/settings', body);
  return res.data.data;
}

export interface DailyEntry {
  id: string;
  metricId: string;
  /** YYYY-MM-DD in the metric's reporting zone. */
  localDate: string;
  weekOf: string;
  value: number;
  notes: string | null;
  enteredViaSource: MetricEntrySource;
  enteredByEmployeeId: string | null;
  computedAt: string | null;
  status: MetricStatus | null;
  createdAt: string;
  updatedAt: string;
}

export interface DailySummary {
  /** The server's idea of today — never derive it client-side. */
  today: string;
  entries: DailyEntry[];
  progress: DailyProgress;
  streak: StreakResult;
  provisional: boolean;
}

export async function getDailySummary(metricId: string, days = 14): Promise<DailySummary> {
  const res = await apiClient.get<{ data: DailySummary }>(
    `/api/scorecards/metrics/${encodeURIComponent(metricId)}/daily?days=${String(days)}`,
  );
  return res.data.data;
}

export interface UpsertDailyRequest {
  /** Omit to log TODAY (the server decides which day that is). */
  localDate?: string;
  value: number;
  notes?: string | null;
}

export async function upsertDailyEntry(
  metricId: string,
  body: UpsertDailyRequest,
): Promise<{ entry: DailyEntry; today: string }> {
  const res = await apiClient.put<{ data: DailyEntry; today: string }>(
    `/api/scorecards/metrics/${encodeURIComponent(metricId)}/daily`,
    body,
  );
  return { entry: res.data.data, today: res.data.today };
}

export async function deleteDailyEntry(metricId: string, localDate: string): Promise<void> {
  await apiClient.delete(
    `/api/scorecards/metrics/${encodeURIComponent(metricId)}/daily/${encodeURIComponent(localDate)}`,
  );
}
